import { useEffect, useMemo, useRef, useState } from 'react';
import { times } from 'lodash';
import styled from 'styled-components';
import { plural } from '@frameio/i18n';
import { Emoji } from '@frameio/shared/emoji';
import { imageLoader } from '@frameio/utils';
import { Avatar, Box, Text, theme, useHoverIntent } from '@frameio/vapor';
import { IconCompareLink, IconPending } from '@frameio/vapor-icons';
import { useGetGroupMembers } from '../../../hooks/useGetGroupMembers';
import { useUpdateMembership } from '../../../hooks/useUpdateMembership';
import { useReadMembershipFragment } from '../../../utils';
import { useGetIsListHalfVisible } from '../hooks/useGetIsListHalfVisible';
import {
  InviteAccessGroupMemberRowSkeleton,
  InviteAccessMemberRoleSelect,
  InviteAccessMemberRow,
} from '../InviteAccessMemberRow';
import {
  StyledDivForAdornmentBefore,
  StyledDivForAdornmentTransitions,
  StyledDivForPending,
  StyledIconChevronRight,
  StyledList,
} from '../styles';
import type { InviteAccessMemberProps } from '../types';
import TEXT from './generated/TEXT/InviteAccessMemberWithMembers';

TEXT.DEFINE({
  /** Label for button to expand and show members of a user group */
  expandLabelGroup: (groupName: string) =>
    `Show group members for ${groupName}`,

  /** Accessibility text for emoji icon */
  emoji: 'Emoji',

  /** Text showing count of group members */
  groupMembersCount: (count: number) =>
    plural(count, {
      one: '1 Group member',
      other: `${count} Group members`,
    }),

  /** Status text indicating group is currently syncing */
  syncingGroup: 'Syncing group',

  /** Label for role selection dropdown for a user group */
  labelRole: (groupName: string) => `Change role for ${groupName}`,

  /** Label indicating current authenticated user */
  labelAuthedUser: '(you)',

  /** Label for pending (not yet accepted invite) user */
  pending: 'Pending User',
});

const AdornmentsContainer = ({
  emoji,
  isHovered,
  isShowing,
  name,
}: {
  emoji?: string | null;
  isHovered: boolean;
  isShowing: boolean;
  name?: string | null;
}) => {
  const showEmoji = !isHovered && !isShowing;

  return (
    <StyledDivForAdornmentBefore>
      <StyledDivForAdornmentTransitions aria-hidden={!showEmoji}>
        <Avatar
          alt={name ?? undefined}
          background="secondary"
          emojiComponent={
            emoji !== undefined && emoji !== null ? (
              <Emoji emoji={emoji} size={13} />
            ) : undefined
          }
          shape="circle"
          size={24}
        />
      </StyledDivForAdornmentTransitions>
      <StyledDivForAdornmentTransitions aria-hidden={showEmoji}>
        <StyledIconChevronRight $rotate={isShowing ? 90 : 0} size={24} />
      </StyledDivForAdornmentTransitions>
    </StyledDivForAdornmentBefore>
  );
};

export const InviteAccessMemberWithMembers = ({
  accessCanBeManaged,
  availableRoles,
  backgroundColor,
  currentUserId,
  deleteMembership,
  isSyncing = false,
  memberCount,
  membershipId,
  queryText = '',
}: InviteAccessMemberProps) => {
  const { member } = useReadMembershipFragment(membershipId);
  const [isShowing, setIsShowing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [didHoverWithIntent, setDidHoverWithIntent] = useState(false);

  const { bind } = useHoverIntent(
    () => {
      if (!didHoverWithIntent) {
        setDidHoverWithIntent(true);
      }
    },
    {
      velocity: 0.2,
    }
  );

  const listRef = useRef<HTMLUListElement>(null);
  const shouldFetch = useGetIsListHalfVisible(listRef.current) && isShowing;

  const {
    loading,
    fetchNextPage: fetchNextGroupPage,
    members,
    variables,
  } = useGetGroupMembers({
    accountUserGroupId: member.accountUserGroup?.id ?? '',
    fetchData: didHoverWithIntent ? true : isShowing,
  });

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const fetchNextPage = fetchNextGroupPage as unknown as () => void;

  useEffect(() => {
    if (!loading && shouldFetch) {
      fetchNextPage();
    }
  }, [fetchNextPage, loading, shouldFetch]);

  const memoizedSkeletons = useMemo(
    () =>
      times(loading ? variables?.page?.first ?? 0 : 0, (index) => (
        <StyledDiv key={index} aria-hidden="true">
          <InviteAccessGroupMemberRowSkeleton />
        </StyledDiv>
      )),
    [loading, variables]
  );

  const [updateMembership, { currentRoleId, currentRoleName }] =
    useUpdateMembership({
      availableRoles: availableRoles ?? [],
      membershipId: member.id,
    });

  const handleUpdateRole = (nextRoleId: string) => {
    updateMembership({ roleId: nextRoleId });
  };

  const handleOnClick = () => {
    setIsShowing((prev) => !prev);
  };

  if (!member.accountUserGroup) {
    return null;
  }

  return (
    <InviteAccessMemberRow
      {...bind}
      adornmentAfter={
        <InviteAccessMemberRoleSelect
          accessCanBeManaged={accessCanBeManaged}
          availableRoles={availableRoles}
          label={
            member.accountUserGroup.name
              ? (TEXT.labelRole(member.accountUserGroup.name) as string)
              : undefined
          }
          onRemove={() =>
            deleteMembership?.({
              membershipId: member?.id,
              memberName: member?.accountUserGroup?.name ?? '',
            })
          }
          onUpdateRole={handleUpdateRole}
          roleId={currentRoleId}
          roleName={currentRoleName}
        />
      }
      adornmentBefore={
        <AdornmentsContainer
          emoji={member.accountUserGroup.emoji}
          isHovered={isHovered}
          isShowing={isShowing}
          name={member?.accountUserGroup?.name}
        />
      }
      backgroundColor={backgroundColor}
      buttonLabel={
        member.accountUserGroup.name
          ? (TEXT.expandLabelGroup(member.accountUserGroup.name) as string)
          : undefined
      }
      collapseContent={
        <>
          <Box ref={listRef} as={StyledList} paddingLeft={32}>
            {members?.map((groupMember) => {
              const nameToDisplay = groupMember.isPending
                ? TEXT.pending
                : groupMember.name;

              const titleSuffix =
                currentUserId === groupMember.userId
                  ? TEXT.labelAuthedUser
                  : '';

              return (
                <Box key={groupMember.id} as="li" paddingLeft={4}>
                  <InviteAccessMemberRow
                    adornmentBefore={
                      groupMember.isPending && (
                        <StyledDivForPending $showBackground={true}>
                          <IconPending
                            color={theme.colors.text.tertiary}
                            size={16}
                          />
                        </StyledDivForPending>
                      )
                    }
                    avatarSrc={
                      groupMember.profileImageUrl
                        ? imageLoader({
                            src: groupMember.profileImageUrl,
                            width: 64,
                          })
                        : undefined
                    }
                    metaContent={
                      <Text
                        color="tertiary"
                        truncate={true}
                        variant="secondary"
                      >
                        {groupMember.email}
                      </Text>
                    }
                    title={`${nameToDisplay} ${titleSuffix}`.trim()}
                  />
                </Box>
              );
            })}
          </Box>
          {!queryText && memoizedSkeletons}
        </>
      }
      metaContent={
        isSyncing ? (
          <Box alignItems="center" display="flex" gap={4}>
            <Text color="tertiary" truncate={true} variant="secondary">
              {TEXT.syncingGroup}
            </Text>
            <IconCompareLink
              color={theme.colors.icon.interactive.notice}
              size={16}
            />
          </Box>
        ) : (
          <Text color="tertiary" truncate={true} variant="secondary">
            {memberCount ? TEXT.groupMembersCount(memberCount) : undefined}
          </Text>
        )
      }
      onClick={(memberCount ?? 0) > 0 ? handleOnClick : undefined}
      onHover={
        (memberCount ?? 0) > 0
          ? (nextIsHovered?: boolean) => setIsHovered(Boolean(nextIsHovered))
          : undefined
      }
      queryText={queryText}
      showCollapseContent={isShowing}
      title={member.accountUserGroup.name ?? ''}
    />
  );
};

const StyledDiv = styled.div`
  padding: ${theme.spaces[4]} ${theme.spaces[2]} ${theme.spaces[4]} 40px;
`;
